/**
 * Store de estado para autenticación
 * @module stores/useAuthStore
 */

import { create } from 'zustand'
import { authService, User } from '@/services/authService'

interface AuthStore {
  // Estado
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null

  // Acciones
  init: () => Promise<void>
  login: (email: string, password: string, tenantSlug: string) => Promise<void>
  register: (email: string, password: string, name: string, tenantSlug: string) => Promise<void>
  acceptInvite: (token: string, password: string, name: string) => Promise<void>
  logout: () => void
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthStore>((set) => ({
  // Estado inicial
  user: authService.getUser(),
  isAuthenticated: authService.isAuthenticated(),
  loading: false,
  error: null,

  // Inicializar - verificar token al cargar la app
  init: async () => {
    if (!authService.isAuthenticated()) {
      set({ user: null, isAuthenticated: false })
      return
    }

    set({ loading: true })
    try {
      const user = await authService.getCurrentUser()
      set({
        user,
        isAuthenticated: !!user,
        loading: false
      })
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        loading: false
      })
    }
  },

  // Login
  login: async (email: string, password: string, tenantSlug: string) => {
    set({ loading: true, error: null })
    try {
      const response = await authService.login({ email, password, tenantSlug })
      set({
        user: response.user,
        isAuthenticated: true,
        loading: false
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al iniciar sesión',
        loading: false
      })
      throw error
    }
  },

  acceptInvite: async (token: string, password: string, name: string) => {
    set({ loading: true, error: null })
    try {
      const response = await authService.acceptInvite({ token, password, name })
      set({
        user: response.user,
        isAuthenticated: true,
        loading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Invitación inválida',
        loading: false,
      })
      throw error
    }
  },

  // Register
  register: async (email: string, password: string, name: string, tenantSlug: string) => {
    set({ loading: true, error: null })
    try {
      const response = await authService.register({ email, password, name, tenantSlug })
      set({
        user: response.user,
        isAuthenticated: true,
        loading: false
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al registrarse',
        loading: false
      })
      throw error
    }
  },

  // Logout
  logout: () => {
    authService.logout()
    set({
      user: null,
      isAuthenticated: false,
      error: null
    })
  },

  // Change password
  changePassword: async (currentPassword: string, newPassword: string) => {
    set({ loading: true, error: null })
    try {
      await authService.changePassword(currentPassword, newPassword)
      set({ loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al cambiar contraseña',
        loading: false
      })
      throw error
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null })
  }
}))
